import { FC, useEffect, useMemo } from 'react';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { Label } from '@/components/UI/Label/Label';
import Notification from '@/components/UI/Notification/Notification';
import {
  gefilteredActiviteiten,
  mapEnFilterActiviteitOptionsTot,
  mapEnFilterActiviteitOptionsVan,
} from '@/utils/helpers';

import { useVerzwakDialog } from './useVerzwakDialog';

import styles from '@/components/UI/Dialog/Dialog.module.css';

type VerzwakDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  details: WerklijnContextMenuDetails;
};

const VerzwakDialog: FC<VerzwakDialogProps> = ({ showDialog, handleOpenChange, details }) => {
  const {
    onSubmit,
    handleSubmit,
    control,
    resetDialog,
    fetchActiviteitenVoorBewegingData,
    clearErrors,
    activiteitenVoorBewegingData,
    setValue,
    watch,
    errorMessage,
    warningMessage,
  } = useVerzwakDialog(details, handleOpenChange);

  const watchActiviteitVan = watch('van');
  const watchActiviteitTot = watch('tot');

  // alle activiteiten voor beide van en tot dropdowns
  const activiteitenOptions = useMemo(() => {
    if (activiteitenVoorBewegingData !== null && activiteitenVoorBewegingData !== undefined) {
      return gefilteredActiviteiten(activiteitenVoorBewegingData, details, true);
    }
  }, [activiteitenVoorBewegingData]);

  // map activiteitenVoorBeweging naar de opties in de Van-dropdown
  const activiteitenOptionsVan = useMemo(() => {
    return mapEnFilterActiviteitOptionsVan(activiteitenOptions);
  }, [activiteitenOptions]);

  // map activiteiten van OptionsVan to options in Tot-dropdown -- indexVan + 1 omdat we de geselecteerde optie in de Van dropdown niet willen tonen in de Tot dropdown
  const activiteitenOptionsTot = useMemo(() => {
    return mapEnFilterActiviteitOptionsTot(activiteitenOptions, watchActiviteitVan);
  }, [activiteitenOptions, watchActiviteitVan]);

  useEffect(() => {
    if (!showDialog) {
      resetDialog(false);
    } else {
      clearErrors();
      fetchActiviteitenVoorBewegingData(`/bewegingen/activiteiten/${details?.treinnummer}/${details?.treindatum}`);
    }
  }, [showDialog]);

  // set default values for Van- and Tot-dropdown
  useEffect(() => {
    if (activiteitenOptionsVan) {
      setValue('van', activiteitenOptionsVan[0].value);
    }
    if (activiteitenOptionsTot) {
      setValue('tot', activiteitenOptionsTot[activiteitenOptionsTot.length - 1].value);
    }
  }, [activiteitenOptions]);

  // Wanneer de value de Van-dropdown groter is dan de value van de Tot-dropdown, set de value van de Tot-dropdown op de laatste optie in de Van-dropdown
  useEffect(() => {
    if (watchActiviteitTot && watchActiviteitVan) {
      if (watchActiviteitVan?.volgnummer > watchActiviteitTot?.volgnummer) {
        setValue('tot', activiteitenOptionsTot?.[activiteitenOptionsTot?.length - 1].value);
      } else if (watchActiviteitVan?.volgnummer === watchActiviteitTot?.volgnummer) {
        setValue('tot', undefined);
      }
    }
  }, [watchActiviteitVan, watchActiviteitTot, activiteitenOptionsVan]);

  return (
    <MwdDialog
      title='Verzwakken'
      className={styles.verzwakkenDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid={`contextMenu-verzwakken-${details?.eenheidnummer}-${details?.treinnummer}`}
      hasCloseInHeader={true}
      // error message has priority over warning message
      showNotification={!!errorMessage || warningMessage}
      notificationType={errorMessage ? 'error' : 'warning'}
      notificationText={errorMessage || 'Let op: de werklijnversie is gewijzigd'}
    >
      {!details.versterkt && (
        <Notification style={{ height: '48px', marginBottom: '12px' }} iconAlignment='center'>
          Deze inzet is niet versterkt
        </Notification>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Label>Treinnummer</Label>
          <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '12px' }}>
            {details.treinnummer}
            {details.inzetPositie ? `-${details.inzetPositie}` : ''} {details.versterkt ? '+' : ''}
          </div>
        </div>
        <div>
          <Label>Eenheid</Label>
          <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '12px' }}>
            {details.eenheidnummer} ({details.materieelSoort}-{details.materieelAanduiding})
          </div>
          <div
            style={{
              height: '2px',
              marginBottom: '12px',
              marginTop: '12px',
              width: '318px',
              background: 'var(--tractie-color-border-subtle)',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <SelectField
            placeholder='Van...'
            control={control}
            testId='van-select'
            required
            name='van'
            label='Van'
            options={activiteitenOptionsVan}
          />
          <SelectField
            placeholder='Tot...'
            control={control}
            testId='tot-select'
            required
            name='tot'
            label='Tot'
            options={activiteitenOptionsTot}
          />
        </div>
        <MwdDialog.Footer
          submitCaption='Verzwak inzet'
          confirmButtonType='destructive'
          cancelCallback={() => resetDialog(true)}
        />
      </form>
    </MwdDialog>
  );
};

export default VerzwakDialog;
